export interface ILangTranslation {
  en: string;
  es: string;
}

export const SUPPORTED_LANGUAGES = [
  'en',
  'es',
  'de',
  'fr',
  'zh',
  'it',
  'pt',
  'ja',
  'nl',
  'ru',
  'ko',
  'pl',
  'sv',
  'tr',
  'ar',
  'vi',
  'id',
  'da',
  'no',
  'fi',
  'cs',
  'el',
  'hu',
  'ro',
  'uk',
  'he',
  'th',
  'hi',
  'ca',
  'bg',
];

export interface ILanguageData {
  nativeName: string;
  flag: string;
  code: string;
  i: number;
  llmKnowledge: number;
  countries: string[];
  translations: ILangTranslation;
  flagImg?: string;
  status: 'ready' | 'commingsoon' | 'almostready';
  speakers: number;
}

export const LANGUAGES: Record<string, ILanguageData> = {
  en: {
    nativeName: 'English',
    flag: '🇺🇸', // Using US flag as a primary example
    flagImg: 'assets/flags/us.svg',
    code: 'en',
    i: 1,
    llmKnowledge: 100,
    countries: ['United Kingdom', 'United States', 'Canada', 'Australia', 'New Zealand', 'Ireland', 'South Africa', 'India', 'and many others.'],
    translations: {
      en: 'English',
      es: 'Inglés',
    },
    status: 'ready',
    speakers: 1500000000,
  },
  es: {
    nativeName: 'Español',
    flag: '🇪🇸', // Using Spain flag as a primary example
    flagImg: 'assets/flags/es.svg',
    code: 'es',
    i: 2,
    llmKnowledge: 99,
    countries: [
      'Spain',
      'Mexico',
      'Argentina',
      'Colombia',
      'Peru',
      'Chile',
      'Venezuela',
      'Ecuador',
      'Guatemala',
      'Cuba',
      'Bolivia',
      'Dominican Republic',
      'Honduras',
      'El Salvador',
      'Nicaragua',
      'Paraguay',
      'Costa Rica',
      'Puerto Rico',
      'Uruguay',
      'Equatorial Guinea.',
    ],
    translations: {
      en: 'Spanish',
      es: 'Español',
    },
    status: 'almostready',
    speakers: 592000000,
  },
  de: {
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    flagImg: 'assets/flags/de.svg',
    code: 'de',
    i: 3,
    llmKnowledge: 97,
    countries: ['Germany', 'Austria', 'Switzerland', 'Belgium', 'Luxembourg', 'Liechtenstein.'],
    translations: {
      en: 'German',
      es: 'Alemán',
    },
    status: 'almostready',
    speakers: 135000000,
  },
  fr: {
    nativeName: 'Français',
    flag: '🇫🇷',
    flagImg: 'assets/flags/fr.svg',
    code: 'fr',
    i: 4,
    llmKnowledge: 96,
    countries: ['France', 'Canada', 'Belgium', 'Switzerland', 'Haiti', 'Ivory Coast', 'Senegal', 'Cameroon', 'Madagascar', 'and many others.'],
    translations: {
      en: 'French',
      es: 'Francés',
    },
    status: 'almostready',
    speakers: 300000000,
  },
  zh: {
    nativeName: '中文 (Zhōngwén)',
    flag: '🇨🇳',
    flagImg: 'assets/flags/cn.svg',
    code: 'zh',
    i: 5,
    llmKnowledge: 94,
    countries: ['China', 'Singapore', 'Taiwan.'],
    translations: {
      en: 'Chinese (Mandarin)',
      es: 'Chino (Mandarín)',
    },
    status: 'almostready',
    speakers: 1120000000,
  },
  it: {
    nativeName: 'Italiano',
    flag: '🇮🇹',
    flagImg: 'assets/flags/it.svg',
    code: 'it',
    i: 6,
    llmKnowledge: 93,
    countries: ['Italy', 'Switzerland', 'San Marino', 'Vatican City.'],
    translations: {
      en: 'Italian',
      es: 'Italiano',
    },
    status: 'almostready',
    speakers: 67000000,
  },
  pt: {
    nativeName: 'Português',
    flag: '🇧🇷',
    flagImg: 'assets/flags/br.svg',
    code: 'pt',
    i: 7,
    llmKnowledge: 92,
    countries: ['Portugal', 'Brazil', 'Angola', 'Mozambique', 'Cape Verde', 'Guinea-Bissau', 'Sao Tome and Principe', 'East Timor', 'Macau.'],
    translations: {
      en: 'Portuguese',
      es: 'Portugués',
    },
    status: 'ready',
    speakers: 258000000,
  },
  ja: {
    nativeName: '日本語 (Nihongo)',
    flag: '🇯🇵',
    flagImg: 'assets/flags/jp.svg',
    code: 'ja',
    i: 8,
    llmKnowledge: 91,
    countries: ['Japan.'],
    translations: {
      en: 'Japanese',
      es: 'Japonés',
    },
    status: 'almostready',
    speakers: 125000000,
  },
  nl: {
    nativeName: 'Nederlands',
    flag: '🇳🇱',
    flagImg: 'assets/flags/nl.svg',
    code: 'nl',
    i: 9,
    llmKnowledge: 90,
    countries: ['Netherlands', 'Belgium', 'Suriname', 'Aruba', 'Curaçao', 'Sint Maarten.'],
    translations: {
      en: 'Dutch',
      es: 'Neerlandés',
    },
    status: 'commingsoon',
    speakers: 25000000,
  },
  ru: {
    nativeName: 'Русский (Russkiy)',
    flag: '🇷🇺',
    flagImg: 'assets/flags/ru.svg',
    code: 'ru',
    i: 10, // Adjusted index
    llmKnowledge: 89,
    countries: ['Russia', 'Belarus', 'Kazakhstan', 'Kyrgyzstan', 'Tajikistan (co-official)', 'Abkhazia', 'South Ossetia', 'Transnistria.'],
    translations: {
      en: 'Russian',
      es: 'Ruso',
    },
    status: 'commingsoon',
    speakers: 258000000,
  },
  ko: {
    nativeName: '한국어 (Hangugeo)',
    flag: '🇰🇷',
    flagImg: 'assets/flags/kr.svg',
    code: 'ko',
    i: 11,
    llmKnowledge: 87,
    countries: ['South Korea', 'North Korea.'],
    translations: {
      en: 'Korean',
      es: 'Coreano',
    },
    status: 'almostready',
    speakers: 82000000,
  },
  pl: {
    nativeName: 'Polski',
    flag: '🇵🇱',
    flagImg: 'assets/flags/pl.svg',
    code: 'pl',
    i: 12,
    llmKnowledge: 86,
    countries: ['Poland.'],
    translations: {
      en: 'Polish',
      es: 'Polaco',
    },
    status: 'commingsoon',
    speakers: 40000000,
  },
  sv: {
    nativeName: 'Svenska',
    flag: '🇸🇪',
    flagImg: 'assets/flags/se.svg',
    code: 'sv',
    i: 13,
    llmKnowledge: 85,
    countries: ['Sweden', 'Finland (co-official).'],
    translations: {
      en: 'Swedish',
      es: 'Sueco',
    },
    status: 'commingsoon',
    speakers: 10000000,
  },
  tr: {
    nativeName: 'Türkçe',
    flag: '🇹🇷',
    flagImg: 'assets/flags/tr.svg',
    code: 'tr',
    i: 14,
    llmKnowledge: 84,
    countries: ['Turkey', 'Cyprus (North).'],
    translations: {
      en: 'Turkish',
      es: 'Turco',
    },
    status: 'commingsoon',
    speakers: 88000000,
  },
  ar: {
    nativeName: 'العربية (Al-Arabiyyah)',
    flag: '🇸🇦',
    flagImg: 'assets/flags/sa.svg',
    code: 'ar',
    i: 15,
    llmKnowledge: 83,
    countries: [
      'Saudi Arabia',
      'Egypt',
      'Algeria',
      'Morocco',
      'Iraq',
      'Sudan',
      'Yemen',
      'Syria',
      'Tunisia',
      'Jordan',
      'United Arab Emirates',
      'Lebanon',
      'Libya',
      'Oman',
      'Kuwait',
      'Mauritania',
      'Qatar',
      'Bahrain',
      'Comoros',
      'Djibouti',
      'Somalia',
      'Eritrea',
      'Chad',
      'Western Sahara',
      'Palestine.',
    ],
    translations: {
      en: 'Arabic',
      es: 'Árabe',
    },
    status: 'commingsoon',
    speakers: 422000000,
  },
  vi: {
    nativeName: 'Tiếng Việt',
    flag: '🇻🇳',
    flagImg: 'assets/flags/vn.svg',
    code: 'vi',
    i: 16,
    llmKnowledge: 82,
    countries: ['Vietnam.'],
    translations: {
      en: 'Vietnamese',
      es: 'Vietnamita',
    },
    status: 'commingsoon',
    speakers: 90000000,
  },
  id: {
    nativeName: 'Bahasa Indonesia',
    flag: '🇮🇩',
    flagImg: 'assets/flags/id.svg',
    code: 'id',
    i: 17,
    llmKnowledge: 81,
    countries: ['Indonesia.'],
    translations: {
      en: 'Indonesian',
      es: 'Indonesio',
    },
    status: 'commingsoon',
    speakers: 199000000,
  },
  da: {
    nativeName: 'Dansk',
    flag: '🇩🇰',
    flagImg: 'assets/flags/dk.svg',
    code: 'da',
    i: 18,
    llmKnowledge: 80,
    countries: ['Denmark', 'Faroe Islands (co-official)', 'Greenland (co-official).'],
    translations: {
      en: 'Danish',
      es: 'Danés',
    },
    status: 'commingsoon',
    speakers: 6000000,
  },
  no: {
    nativeName: 'Norsk',
    flag: '🇳🇴',
    flagImg: 'assets/flags/no.svg',
    code: 'no',
    i: 19,
    llmKnowledge: 79,
    countries: ['Norway.'],
    translations: {
      en: 'Norwegian',
      es: 'Noruego',
    },
    status: 'commingsoon',
    speakers: 5300000,
  },
  fi: {
    nativeName: 'Suomi',
    flag: '🇫🇮',
    flagImg: 'assets/flags/fi.svg',
    code: 'fi',
    i: 20,
    llmKnowledge: 78,
    countries: ['Finland.'],
    translations: {
      en: 'Finnish',
      es: 'Finlandés',
    },
    status: 'commingsoon',
    speakers: 5400000,
  },
  cs: {
    nativeName: 'Čeština',
    flag: '🇨🇿',
    flagImg: 'assets/flags/cz.svg',
    code: 'cs',
    i: 21,
    llmKnowledge: 77,
    countries: ['Czech Republic.'],
    translations: {
      en: 'Czech',
      es: 'Checo',
    },
    status: 'commingsoon',
    speakers: 10700000,
  },
  el: {
    nativeName: 'Ελληνικά (Elliniká)',
    flag: '🇬🇷',
    flagImg: 'assets/flags/gr.svg',
    code: 'el',
    i: 22,
    llmKnowledge: 76,
    countries: ['Greece', 'Cyprus.'],
    translations: {
      en: 'Greek',
      es: 'Griego',
    },
    status: 'commingsoon',
    speakers: 13500000,
  },
  hu: {
    nativeName: 'Magyar',
    flag: '🇭🇺',
    flagImg: 'assets/flags/hu.svg',
    code: 'hu',
    i: 23,
    llmKnowledge: 75,
    countries: ['Hungary.'],
    translations: {
      en: 'Hungarian',
      es: 'Húngaro',
    },
    status: 'commingsoon',
    speakers: 13000000,
  },
  ro: {
    nativeName: 'Română',
    flag: '🇷🇴',
    flagImg: 'assets/flags/ro.svg',
    code: 'ro',
    i: 24,
    llmKnowledge: 74,
    countries: ['Romania', 'Moldova (as Moldovan).'],
    translations: {
      en: 'Romanian',
      es: 'Rumano',
    },
    status: 'commingsoon',
    speakers: 24000000,
  },
  uk: {
    nativeName: 'Українська (Ukrainska)',
    flag: '🇺🇦',
    flagImg: 'assets/flags/ua.svg',
    code: 'uk',
    i: 25,
    llmKnowledge: 73,
    countries: ['Ukraine.'],
    translations: {
      en: 'Ukrainian',
      es: 'Ucraniano',
    },
    status: 'commingsoon',
    speakers: 33000000,
  },
  he: {
    nativeName: 'עברית (Ivrit)',
    flag: '🇮🇱',
    flagImg: 'assets/flags/il.svg',
    code: 'he',
    i: 26,
    llmKnowledge: 72,
    countries: ['Israel.'],
    translations: {
      en: 'Hebrew',
      es: 'Hebreo',
    },
    status: 'commingsoon',
    speakers: 9000000,
  },
  th: {
    nativeName: 'ภาษาไทย (Phasa Thai)',
    flag: '🇹🇭',
    flagImg: 'assets/flags/th.svg',
    code: 'th',
    i: 27,
    llmKnowledge: 71,
    countries: ['Thailand.'],
    translations: {
      en: 'Thai',
      es: 'Tailandés',
    },
    status: 'commingsoon',
    speakers: 69000000,
  },
  hi: {
    nativeName: 'हिन्दी (Hindī)',
    flag: '🇮🇳',
    flagImg: 'assets/flags/in.svg',
    code: 'hi',
    i: 28,
    llmKnowledge: 70,
    countries: ['India (co-official)', 'Fiji (co-official).'],
    translations: {
      en: 'Hindi',
      es: 'Hindi',
    },
    status: 'commingsoon',
    speakers: 602000000,
  },
  ca: {
    nativeName: 'Català',
    flag: '🇪🇸',
    flagImg: 'assets/flags/es.svg',
    code: 'ca',
    i: 29,
    llmKnowledge: 69,
    countries: ['Spain (Catalonia, Valencia, Balearic Islands)', 'Andorra.'],
    translations: {
      en: 'Catalan',
      es: 'Catalán',
    },
    status: 'commingsoon',
    speakers: 10000000,
  },
  bg: {
    nativeName: 'Български (Balgarski)',
    flag: '🇧🇬',
    flagImg: 'assets/flags/bg.svg',
    code: 'bg',
    i: 30,
    llmKnowledge: 68,
    countries: ['Bulgaria.'],
    translations: {
      en: 'Bulgarian',
      es: 'Búlgaro',
    },
    status: 'commingsoon',
    speakers: 9000000,
  },
  eo: {
    nativeName: 'Esperanto',
    flag: '🇺🇳', // Usando la bandera de la ONU por su naturaleza internacional
    flagImg: 'assets/flags/eo.svg',
    code: 'eo',
    i: 59,
    llmKnowledge: 90,
    countries: ['Worldwide (constructed language)'],
    translations: {
      en: 'Esperanto',
      es: 'Esperanto',
    },
    status: 'commingsoon',
    speakers: 2000000,
  },
  ia: {
    nativeName: 'Interlingua',
    flag: '🇺🇳', // Usando la bandera de la ONU por su naturaleza internacional
    flagImg: 'assets/flags/ia.svg',
    code: 'ia',
    i: 60,
    llmKnowledge: 85,
    countries: ['Worldwide (constructed language)'],
    translations: {
      en: 'Interlingua',
      es: 'Interlingua',
    },
    status: 'commingsoon',
    speakers: 1500,
  },
  hr: {
    nativeName: 'Hrvatski',
    flag: '🇭🇷',
    flagImg: 'assets/flags/hr.svg',
    code: 'hr',
    i: 31,
    llmKnowledge: 67,
    countries: ['Croatia', 'Bosnia and Herzegovina (co-official)', 'Serbia (Vojvodina).'],
    translations: {
      en: 'Croatian',
      es: 'Croata',
    },
    status: 'commingsoon',
    speakers: 5700000,
  },
  sk: {
    nativeName: 'Slovenčina',
    flag: '🇸🇰',
    flagImg: 'assets/flags/sk.svg',
    code: 'sk',
    i: 32,
    llmKnowledge: 66,
    countries: ['Slovakia.'],
    translations: {
      en: 'Slovak',
      es: 'Eslovaco',
    },
    status: 'commingsoon',
    speakers: 5400000,
  },
  fil: {
    nativeName: 'Filipino',
    flag: '🇵🇭',
    flagImg: 'assets/flags/ph.svg',
    code: 'fil',
    i: 33,
    llmKnowledge: 65,
    countries: ['Philippines (co-official).'],
    translations: {
      en: 'Filipino',
      es: 'Filipino',
    },
    status: 'commingsoon',
    speakers: 109000000,
  },
  tl: {
    // Tagalog often used as base for Filipino, but distinct ISO code
    nativeName: 'Tagalog',
    flag: '🇵🇭',
    flagImg: 'assets/flags/ph.svg',
    code: 'tl',
    i: 34,
    llmKnowledge: 64,
    countries: ['Philippines (base of Filipino).'],
    translations: {
      en: 'Tagalog',
      es: 'Tagalo',
    },
    status: 'commingsoon',
    speakers: 109000000,
  },
  lt: {
    nativeName: 'Lietuvių',
    flag: '🇱🇹',
    flagImg: 'assets/flags/lt.svg',
    code: 'lt',
    i: 35,
    llmKnowledge: 63,
    countries: ['Lithuania.'],
    translations: {
      en: 'Lithuanian',
      es: 'Lituano',
    },
    status: 'commingsoon',
    speakers: 2800000,
  },
  lv: {
    nativeName: 'Latviešu',
    flag: '🇱🇻',
    flagImg: 'assets/flags/lv.svg',
    code: 'lv',
    i: 36,
    llmKnowledge: 62,
    countries: ['Latvia.'],
    translations: {
      en: 'Latvian',
      es: 'Letón',
    },
    status: 'commingsoon',
    speakers: 1900000,
  },
  sl: {
    nativeName: 'Slovenščina',
    flag: '🇸🇮',
    flagImg: 'assets/flags/si.svg',
    code: 'sl',
    i: 37,
    llmKnowledge: 61,
    countries: ['Slovenia.'],
    translations: {
      en: 'Slovenian',
      es: 'Esloveno',
    },
    status: 'commingsoon',
    speakers: 2500000,
  },
  et: {
    nativeName: 'Eesti',
    flag: '🇪🇪',
    flagImg: 'assets/flags/ee.svg',
    code: 'et',
    i: 38,
    llmKnowledge: 60,
    countries: ['Estonia.'],
    translations: {
      en: 'Estonian',
      es: 'Estonio',
    },
    status: 'commingsoon',
    speakers: 1100000,
  },
  ms: {
    nativeName: 'Bahasa Melayu',
    flag: '🇲🇾',
    flagImg: 'assets/flags/my.svg',
    code: 'ms',
    i: 39,
    llmKnowledge: 59,
    countries: ['Malaysia', 'Brunei', 'Singapore (co-official).'],
    translations: {
      en: 'Malay',
      es: 'Malayo',
    },
    status: 'commingsoon',
    speakers: 290000000,
  },
  sw: {
    nativeName: 'Kiswahili',
    flag: '🇹🇿',
    flagImg: 'assets/flags/tz.svg',
    code: 'sw',
    i: 40,
    llmKnowledge: 58,
    countries: ['Tanzania', 'Kenya', 'Uganda', 'Rwanda', 'Democratic Republic of the Congo (national language).'],
    translations: {
      en: 'Swahili',
      es: 'Suajili',
    },
    status: 'commingsoon',
    speakers: 200000000,
  },
  fa: {
    nativeName: 'فارسی (Fārsī)',
    flag: '🇮🇷',
    flagImg: 'assets/flags/ir.svg',
    code: 'fa',
    i: 41,
    llmKnowledge: 57,
    countries: ['Iran', 'Afghanistan (as Dari)', 'Tajikistan (as Tajik).'],
    translations: {
      en: 'Persian/Farsi',
      es: 'Persa',
    },
    status: 'commingsoon',
    speakers: 110000000,
  },
  bn: {
    nativeName: 'বাংলা (Bangla)',
    flag: '🇧🇩',
    flagImg: 'assets/flags/bd.svg',
    code: 'bn',
    i: 42,
    llmKnowledge: 56,
    countries: ['Bangladesh', 'India (West Bengal, Tripura).'],
    translations: {
      en: 'Bengali',
      es: 'Bengalí',
    },
    status: 'commingsoon',
    speakers: 272000000,
  },
  ur: {
    nativeName: 'اردو (Urdū)',
    flag: '🇵🇰',
    flagImg: 'assets/flags/pk.svg',
    code: 'ur',
    i: 43,
    llmKnowledge: 55,
    countries: ['Pakistan (co-official)', 'India (recognized).'],
    translations: {
      en: 'Urdu',
      es: 'Urdu',
    },
    status: 'commingsoon',
    speakers: 231000000,
  },
  sr: {
    nativeName: 'Српски (Srpski)',
    flag: '🇷🇸',
    flagImg: 'assets/flags/rs.svg',
    code: 'sr',
    i: 44,
    llmKnowledge: 54,
    countries: ['Serbia', 'Bosnia and Herzegovina (co-official)', 'Montenegro (co-official)', 'Kosovo (co-official).'],
    translations: {
      en: 'Serbian',
      es: 'Serbio',
    },
    status: 'commingsoon',
    speakers: 9000000,
  },
  is: {
    nativeName: 'Íslenska',
    flag: '🇮🇸',
    flagImg: 'assets/flags/is.svg',
    code: 'is',
    i: 45,
    llmKnowledge: 53,
    countries: ['Iceland.'],
    translations: {
      en: 'Icelandic',
      es: 'Islandés',
    },
    status: 'commingsoon',
    speakers: 314000,
  },
  ta: {
    nativeName: 'தமிழ் (Tamiḻ)',
    flag: '🇱🇰',
    flagImg: 'assets/flags/lk.svg',
    code: 'ta',
    i: 46,
    llmKnowledge: 52,
    countries: ['Sri Lanka', 'Singapore', 'India (Tamil Nadu, Puducherry).'],
    translations: {
      en: 'Tamil',
      es: 'Tamil',
    },
    status: 'commingsoon',
    speakers: 86000000,
  },
  mr: {
    nativeName: 'मराठी (Marāṭhī)',
    flag: '🇮🇳',
    flagImg: 'assets/flags/in.svg',
    code: 'mr',
    i: 47,
    llmKnowledge: 51,
    countries: ['India (Maharashtra).'],
    translations: {
      en: 'Marathi',
      es: 'Maratí',
    },
    status: 'commingsoon',
    speakers: 99000000,
  },
  my: {
    nativeName: 'မြန်မာဘာသာ (Myanmar)',
    flag: '🇲🇲',
    flagImg: 'assets/flags/mm.svg',
    code: 'my',
    i: 48,
    llmKnowledge: 50,
    countries: ['Myanmar.'],
    translations: {
      en: 'Burmese',
      es: 'Birmano',
    },
    status: 'commingsoon',
    speakers: 43000000,
  },
  cy: {
    nativeName: 'Cymraeg',
    flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
    flagImg: 'assets/flags/cy.svg',
    code: 'cy',
    i: 49,
    llmKnowledge: 49,
    countries: ['United Kingdom (Wales).'],
    translations: {
      en: 'Welsh',
      es: 'Galés',
    },
    status: 'commingsoon',
    speakers: 724000,
  },
  ha: {
    nativeName: 'Hausa',
    flag: '🇳🇬',
    flagImg: 'assets/flags/ng.svg',
    code: 'ha',
    i: 50,
    llmKnowledge: 48,
    countries: ['Nigeria (major language)', 'Niger', 'Ghana', 'Cameroon', 'Benin', 'Chad', 'Sudan.'],
    translations: {
      en: 'Hausa',
      es: 'Hausa',
    },
    status: 'commingsoon',
    speakers: 130000000,
  },
  eu: {
    nativeName: 'Euskara',
    flag: '🇪🇸',
    flagImg: 'assets/flags/es.svg',
    code: 'eu',
    i: 51,
    llmKnowledge: 47,
    countries: ['Spain (Basque Country, Navarre)', 'France (French Basque Country).'],
    translations: {
      en: 'Basque',
      es: 'Vasco',
    },
    status: 'commingsoon',
    speakers: 750000,
  },
  ga: {
    nativeName: 'Gaeilge',
    flag: '🇮🇪',
    flagImg: 'assets/flags/ie.svg',
    code: 'ga',
    i: 52,
    llmKnowledge: 46,
    countries: ['Ireland', 'United Kingdom (Northern Ireland).'],
    translations: {
      en: 'Irish',
      es: 'Irlandés',
    },
    status: 'commingsoon',
    speakers: 1900000,
  },
  sd: {
    nativeName: 'سنڌي (Sindhī)',
    flag: '🇵🇰',
    flagImg: 'assets/flags/pk.svg',
    code: 'sd',
    i: 53,
    llmKnowledge: 45,
    countries: ['Pakistan (Sindh)', 'India (recognized).'],
    translations: {
      en: 'Sindhi',
      es: 'Sindi',
    },
    status: 'commingsoon',
    speakers: 32000000,
  },
  am: {
    nativeName: 'አማርኛ (Amarəñña)',
    flag: '🇪🇹',
    flagImg: 'assets/flags/et.svg',
    code: 'am',
    i: 54,
    llmKnowledge: 44,
    countries: ['Ethiopia.'],
    translations: {
      en: 'Amharic',
      es: 'Amárico',
    },
    status: 'commingsoon',
    speakers: 57000000,
  },
  jv: {
    nativeName: 'Basa Jawa',
    flag: '🇮🇩',
    flagImg: 'assets/flags/id.svg',
    code: 'jv',
    i: 55,
    llmKnowledge: 43,
    countries: ['Indonesia (important regional language).'],
    translations: {
      en: 'Javanese',
      es: 'Javanés',
    },
    status: 'commingsoon',
    speakers: 68000000,
  },
  km: {
    nativeName: 'ភាសាខ្មែរ (Phéasa Khmér)',
    flag: '🇰🇭',
    flagImg: 'assets/flags/kh.svg',
    code: 'km',
    i: 56,
    llmKnowledge: 42,
    countries: ['Cambodia.'],
    translations: {
      en: 'Khmer',
      es: 'Jemer',
    },
    status: 'commingsoon',
    speakers: 16000000,
  },
  yo: {
    nativeName: 'Yorùbá',
    flag: '🇳🇬',
    flagImg: 'assets/flags/ng.svg',
    code: 'yo',
    i: 57,
    llmKnowledge: 41,
    countries: ['Nigeria', 'Benin', 'Togo.'],
    translations: {
      en: 'Yoruba',
      es: 'Yoruba',
    },
    status: 'commingsoon',
    speakers: 50000000,
  },
  gl: {
    nativeName: 'Galego',
    flag: '🇪🇸',
    flagImg: 'assets/flags/es.svg',
    code: 'gl',
    i: 58,
    llmKnowledge: 40,
    countries: ['Spain (Galicia).'],
    translations: {
      en: 'Galician',
      es: 'Gallego',
    },
    status: 'commingsoon',
    speakers: 2400000,
  },
};

export function getLangDesc(langCode: string, lang: string): string {
  return LANGUAGES[langCode]?.translations[lang] || langCode;
}

export function getSupportedLanguageOptions(lang: 'es' | 'en' = 'es') {
  return SUPPORTED_LANGUAGES.map((langCode) => ({
    value: langCode,
    label: getLangDesc(langCode, lang),
  }));
}
